import { NavLink, Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useHealth, useMe, useLogout, useCentralizedRequests } from '../lib/api';
import clsx from 'clsx';
import { NAV_SECTIONS, navCount, activeNav } from '../lib/nav.js';

const NAV_ITEMS = [
  { to: '/',                     label: 'Live Ops',           icon: '🟢' },
  { to: '/trips',                label: 'Trip Requests',      icon: '📋' },
  { to: '/centralized-requests', label: 'Centralized Requests', icon: '📥' },
  { to: '/trs',                  label: 'TRS Verdicts',       icon: '🧭' },
  { to: '/anomalies',            label: 'Anomalies',          icon: '🚨' },
  { to: '/batches',              label: 'Batches',            icon: '📈' },
  { to: '/config',               label: 'Config',             icon: '⚙️' },
];

// Small count pill shown next to a nav item (e.g. held Centralized Requests).
function NavCount({ count }) {
  if (!count) return null;
  return (
    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold leading-none">
      {count}
    </span>
  );
}

function NavTabs({ orientation = 'horizontal', counts = {} }) {
  return (
    <nav className={clsx(
      'flex gap-1',
      orientation === 'horizontal' ? 'flex-row' : 'flex-col w-full'
    )}>
      {NAV_ITEMS.map(item => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          className={({ isActive }) => clsx(
            'px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2',
            isActive
              ? 'bg-tint-fill text-tint'
              : 'text-slate-200 hover:bg-sentinel-navy-700'
          )}
        >
          <span>{item.icon}</span>
          <span>{item.label}</span>
          <NavCount count={counts[item.to]} />
        </NavLink>
      ))}
    </nav>
  );
}

function HealthDot() {
  const { data, isLoading, isError } = useHealth();
  if (isLoading) return <span className="w-2 h-2 rounded-full bg-slate-400 animate-pulse" title="Checking…" />;
  if (isError || !data?.ok) return <span className="w-2 h-2 rounded-full bg-rose-500" title="Disconnected" />;
  return <span className="w-2 h-2 rounded-full bg-emerald-500" title={`OK — ${data.db?.path}`} />;
}

function UserMenu() {
  const { data } = useMe();
  const logout = useLogout();
  const nav = useNavigate();

  if (!data?.user) return null;

  function handleLogout() {
    logout.mutate(undefined, {
      onSuccess: () => nav('/login', { replace: true }),
    });
  }

  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-slate-300 hidden sm:inline">{data.user.username}</span>
      <button
        onClick={handleLogout}
        disabled={logout.isPending}
        className="px-2 py-1 text-xs rounded text-slate-200 hover:bg-sentinel-navy-700 disabled:opacity-50"
        title="Sign out"
      >
        {logout.isPending ? '…' : 'Sign out'}
      </button>
    </div>
  );
}

const ROW_BASE = 'h-8 px-3 rounded-md text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors';

function SidebarCount({ count }) {
  if (!count) return null;
  return (
    <span className="ml-auto px-1.5 py-0.5 rounded-full bg-attention-fill text-attention text-[10px] font-semibold leading-none tabular-nums">
      {count}
    </span>
  );
}

function SidebarLink({ item, counts, indent = false }) {
  const n = navCount(item, counts);
  // lib/nav.js activeNav() is the single source of truth for which row is lit.
  const { pathname } = useLocation();
  const isActive = activeNav(pathname)?.row === item.id;
  return (
    <Link
      to={item.to}
      aria-current={isActive ? 'page' : undefined}
      className={clsx(
        ROW_BASE,
        indent && 'pl-7',
        isActive ? 'bg-tint-fill text-tint' : 'text-label hover:bg-fill'
      )}
    >
      <span className="truncate">{item.label}</span>
      <SidebarCount count={n} />
    </Link>
  );
}

function SidebarStatus() {
  const { data, isLoading, isError } = useHealth();
  const state = isLoading ? 'checking' : (isError || !data?.ok) ? 'down' : 'ok';
  const label = { checking: 'Checking…', down: 'Disconnected', ok: 'Connected' }[state];
  const dot = { checking: 'bg-slate-400 animate-pulse', down: 'bg-rose-500', ok: 'bg-emerald-500' }[state];
  return (
    <div className="h-8 px-3 flex items-center gap-2 text-xs text-label-secondary whitespace-nowrap" title={state === 'ok' ? `OK — ${data?.db?.path}` : label}>
      <span className={clsx('w-2 h-2 rounded-full shrink-0', dot)} aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

function SidebarUser() {
  const { data } = useMe();
  const logout = useLogout();
  const nav = useNavigate();
  if (!data?.user) return null;
  function handleLogout() {
    logout.mutate(undefined, { onSuccess: () => nav('/login', { replace: true }) });
  }
  return (
    <div className="h-8 px-3 flex items-center gap-2 text-sm whitespace-nowrap">
      <span className="text-label truncate">{data.user.username}</span>
      <button
        onClick={handleLogout}
        disabled={logout.isPending}
        data-testid="sidebar-sign-out"
        className="ml-auto px-2 py-1 text-xs rounded text-label-secondary hover:bg-fill disabled:opacity-50"
        title="Sign out"
      >
        {logout.isPending ? '…' : 'Sign out'}
      </button>
    </div>
  );
}

// Desktop (>= 1024px) left sidebar. Mobile/tablet keep the top bar below.
function Sidebar({ counts }) {
  return (
    <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[232px] flex-col bg-grouped border-r border-separator">
      <div className="h-14 px-4 flex items-center gap-3 shrink-0">
        <div className="w-8 h-8 rounded bg-tint-bright text-sentinel-navy flex items-center justify-center font-bold text-sm">
          SQ
        </div>
        <div className="font-semibold text-sm text-label whitespace-nowrap">SQP Ops</div>
      </div>
      <nav aria-label="Primary" className="flex-1 overflow-y-auto px-2 py-2 flex flex-col gap-0.5">
        {NAV_SECTIONS.map(s => {
          const n = navCount(s, counts);
          return (
            <div key={s.id} className="flex flex-col gap-0.5">
              {s.to
                ? <SidebarLink item={s} counts={counts} />
                : (
                  <div className={clsx(ROW_BASE, 'text-label')}>
                    <span className="truncate">{s.label}</span>
                    <SidebarCount count={n} />
                  </div>
                )}
              {(s.children || []).map(c => <SidebarLink key={c.id} item={c} counts={counts} indent />)}
            </div>
          );
        })}
      </nav>
      <div className="shrink-0 border-t border-separator px-2 py-2 flex flex-col gap-0.5">
        <SidebarStatus />
        <SidebarUser />
      </div>
    </aside>
  );
}

export default function Layout() {
  // Held Centralized-Request count → nav pill (so the backlog is visible without
  // opening the page). Default status='held'; refetches on the hook's interval.
  const { data: crData } = useCentralizedRequests();
  const navCounts = { '/centralized-requests': crData?.count || 0 };
  // Same held count, keyed for the sidebar model (lib/nav.js). Review/Alerts badges: no client hook
  // in Layout supplies those counts today, so they are intentionally absent.
  const sidebarCounts = { heldIntake: crData?.count || 0 };

  return (
    <div className="min-h-full flex flex-col lg:pl-[232px]">
      <Sidebar counts={sidebarCounts} />
      {/* Top bar — below 1024px only; the sidebar replaces it at lg: */}
      <header className="lg:hidden bg-sentinel-navy text-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-tint-bright text-sentinel-navy flex items-center justify-center font-bold text-sm">
              SQ
            </div>
            <div>
              <div className="font-semibold text-sm leading-tight">SQP Ops</div>
              <div className="text-xs text-slate-300 leading-tight">Sentinel Quoting Pipeline</div>
            </div>
          </div>
          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-4">
            <NavTabs counts={navCounts} />
            <HealthDot />
            <UserMenu />
          </div>
          {/* Mobile: health dot + sign-out (compact) */}
          <div className="md:hidden flex items-center gap-2">
            <HealthDot />
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-[calc(6rem+env(safe-area-inset-bottom))] md:pb-6">
        <Outlet />
      </main>

      {/* Bottom nav — mobile only */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 pb-[env(safe-area-inset-bottom)] bg-sentinel-navy text-white border-t border-sentinel-navy-700 shadow-lg">
        <div className="flex">
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) => clsx(
                'flex-1 py-3 text-center text-xs font-medium flex flex-col items-center gap-0.5 relative',
                isActive ? 'text-tint-bright' : 'text-slate-300'
              )}
            >
              <span className="text-lg">{item.icon}</span>
              <span>{item.label}</span>
              {navCounts[item.to] > 0 && (
                <span className="absolute top-1 right-1/2 translate-x-4 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[9px] font-semibold leading-none">
                  {navCounts[item.to]}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
